import React, { useState, useRef, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Sparkles, FileText, Link2, Upload, X, Info, Lock, GraduationCap, ChevronDown, FileArchive, Youtube, Library, Crown, ChevronRight } from 'lucide-react';
import { api } from '../api';
import { toast } from 'sonner';

const AGES = ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18'];

const PLAN_LIMITS = { free: 1, standard: 1, pro: 3, gold: 10, ultra: 999, institution_pro: 3, institution_gold: 10, institution_ultra: 999 };
const PRO_PLANS = ['pro', 'gold', 'ultra', 'institution_pro', 'institution_gold', 'institution_ultra'];
const GOLD_PLANS = ['gold', 'ultra', 'institution_gold', 'institution_ultra'];

// TERMINOLOGÍA OFICIAL NAP + ORIENTACIONES
const SUBJECTS_BY_LEVEL = {
  inicial: [
    'Prácticas del Lenguaje', 'Matemática', 'Ambiente Natural y Social',
    'Educación Artística', 'Educación Física', 'Pensamiento Computacional / Robótica', 'Otro'
  ],
  primaria: [
    'Prácticas del Lenguaje', 'Matemática', 'Ciencias Naturales', 'Ciencias Sociales',
    'Educación Artística', 'Educación Física', 'Lengua Extranjera: Inglés', 'Educación Digital, Programación y Robótica', 'Formación Ética y Ciudadana', 'Otro'
  ],
  secundaria_basico: [
    'Lengua y Literatura', 'Matemática', 'Físico-Química', 'Biología', 'Historia',
    'Geografía', 'Lengua Extranjera: Inglés', 'Educación Física', 'Educación Artística', 'Educación Digital, Programación y Robótica', 
    'Educación Tecnológica', 'Formación Ética y Ciudadana', 'Otro'
  ],
  superior: [
    'Ciencias de la Salud', 'Ciencias Exactas y Naturales', 'Ciencias Sociales y Humanidades',
    'Ingeniería y Tecnología', 'Ciencias Económicas y Negocios', 'Arte y Diseño', 'Otro'
  ]
};

// Lista de Orientaciones para el Ciclo Orientado
const ORIENTATIONS = [
  'Ciencias Sociales y Humanidades', 'Ciencias Naturales', 'Economía y Administración', 'Lenguas',
  'Arte', 'Agraria / Agro y Ambiente', 'Turismo', 'Comunicación', 'Informática / Programación', 'Educación Física', 'Otro'
];

// Materias comunes que se dan dentro del Ciclo Orientado (independientemente de la orientación)
const ORIENTED_SUBJECTS = [
  'Lengua y Literatura', 'Matemática', 'Física', 'Química', 'Biología', 'Historia',
  'Geografía', 'Lengua Extranjera: Inglés', 'Educación Física', 'Educación Artística', 
  'Formación Ética y Ciudadana / Instrucción Cívica', 'Filosofía', 'Psicología', 'Sociología', 'Economía', 
  'Materia Específica de la Orientación', 'Otro'
];

const CLASS_STRUCTURES = [
  { id: '1_simple', label: '1 Clase Simple (40 min)', tier: 'free' },
  { id: '1_doble', label: '1 Clase Doble (80 min) 🔒 PRO', tier: 'pro' },
  { id: '2_simples', label: '2 Clases Simples (80 min) 🔒 PRO', tier: 'pro' },
  { id: '1_doble_1_simple', label: 'Semana: 1 Doble + 1 Simple (120 min) 🔒 GOLD', tier: 'gold' },
  { id: '2_dobles', label: 'Semana: 2 Dobles (160 min) 🔒 GOLD', tier: 'gold' }
];

export default function GenerationForm({ onGenerate, isLoading, user, onShowPricing, onCreditsUpdate, onOpenUltraPromo }) {
  const [topic, setTopic] = useState('');
  const [customFocus, setCustomFocus] = useState('');
  
  const [age, setAge] = useState('14');
  const [level, setLevel] = useState('secundaria'); 
  const [cycle, setCycle] = useState('basico'); 
  const [orientation, setOrientation] = useState(''); 
  const [subject, setSubject] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [career, setCareer] = useState(''); 
  
  const [classStructure, setClassStructure] = useState('1_simple'); 
  
  const [contextType, setContextType] = useState('text');
  const [contextText, setContextText] = useState('');
  const [contextUrl, setContextUrl] = useState('');
  const [materials, setMaterials] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [repoItems, setRepoItems] = useState([]);
  const [expandedRepoFolders, setExpandedRepoFolders] = useState({});
  const isUltra = ['ultra', 'institution_ultra'].includes(user?.plan);
  
  const fileRef = useRef(null);
  const userPlan = user?.plan || 'free';
  const limit = PLAN_LIMITS[userPlan] || 1;
  const noCredits = user?.credits_remaining === 0 && user?.credits_remaining !== -1;
  const remainingSlots = limit - materials.length;
  
  const isProOrHigher = PRO_PLANS.includes(userPlan);
  const isGoldOrHigher = GOLD_PLANS.includes(userPlan);

  useEffect(() => {
    if (isUltra) {
      api.get('/repository').then(res => setRepoItems(res.data)).catch(() => {});
    }
  }, [isUltra]);

  const toggleRepoFolder = (folderName) => {
    setExpandedRepoFolders(prev => ({ ...prev, [folderName]: !prev[folderName] }));
  };

  const addMaterial = (type, name, content) => {
    if (materials.length >= limit) {
      toast.error(`Tu plan permite máximo ${limit} materiales de contexto.`);
      onShowPricing();
      return;
    }
    if (materials.some(m => m.name === name)) {
      toast.error('Este material ya está agregado a la clase.');
      return;
    }
    setMaterials([...materials, { type, name, content, id: Math.random().toString(36).substr(2, 9) }]);
  };

  const removeMaterial = (id) => {
    setMaterials(materials.filter(m => m.id !== id));
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    if (noCredits) { onShowPricing(); return; }
    if (materials.length >= limit) { onShowPricing(); return; }

    setIsProcessing(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/upload-file', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      addMaterial('file', file.name, data.text);
      if (onCreditsUpdate && data.credits_remaining !== undefined) onCreditsUpdate(data.credits_remaining);
      toast.success('Archivo procesado correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar el archivo');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUrlProcess = async () => {
    if (!contextUrl.trim()) return;
    if (noCredits) { onShowPricing(); return; }
    if (materials.length >= limit) { onShowPricing(); return; }

    setIsProcessing(true);
    try {
      const { data } = await api.post('/scrape-url', { url: contextUrl });
      addMaterial('url', contextUrl, data.text);
      setContextUrl('');
      if (onCreditsUpdate && data.credits_remaining !== undefined) onCreditsUpdate(data.credits_remaining);
      toast.success('Enlace procesado correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar la URL');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddManualText = () => {
    if (!contextText.trim()) return;
    addMaterial('texto', 'Recorte de texto', contextText);
    setContextText('');
  };

  const handleAddFromRepo = (item) => {
    addMaterial('repo', item.file_name, item.content);
    toast.success('Material agregado a la planificación');
  };

  const handleStructureSelect = (e) => {
    const selectedId = e.target.value;
    const struct = CLASS_STRUCTURES.find(s => s.id === selectedId);
    
    if (struct.tier === 'gold' && !isGoldOrHigher) {
      onShowPricing(); 
      return;
    }
    if (struct.tier === 'pro' && !isProOrHigher) {
      onShowPricing(); 
      return;
    }
    setClassStructure(selectedId);
  };

  const handleLevelChange = (newLevel) => {
    setLevel(newLevel);
    setSubject(''); 
    setCustomSubject('');
    setCareer('');
    setOrientation(''); 
    if (newLevel === 'superior') {
      setAge('Universitario / Superior');
    } else {
      setAge(newLevel === 'inicial' ? '5' : newLevel === 'primaria' ? '10' : '14'); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!topic.trim() || !subject.trim()) {
      toast.error('Por favor completá el tema y la materia/área.');
      return;
    }
    
    if (level === 'secundaria' && cycle === 'orientado' && !orientation) {
      toast.error('Por favor seleccioná la orientación de la escuela.');
      return;
    }

    const finalSubject = subject === 'Otro' ? customSubject.trim() : subject;
    if (subject === 'Otro' && !finalSubject) {
      toast.error('Por favor escribí el nombre de la materia.');
      return;
    }

    if (level === 'superior' && !career.trim()) {
      toast.error('Por favor indicá la carrera.');
      return;
    }
    
    let backendLevel = level;
    if (level === 'secundaria') {
        backendLevel = cycle === 'basico' ? 'secundaria_basico' : 'secundaria_orientado';
    }

    let curricularContext = `--- CONTEXTO CURRICULAR ---\nNivel: ${backendLevel.toUpperCase()}`;
    
    if (level === 'secundaria' && cycle === 'orientado') {
      curricularContext += `\nOrientación de la Escuela: ${orientation}`;
    }
    
    curricularContext += `\nÁrea/Materia: ${finalSubject}`;
    if (level === 'superior') curricularContext += `\nCarrera: ${career}`;

    const attachedMaterials = materials.map(m => `--- MATERIAL DE BIBLIOGRAFÍA (${m.type}: ${m.name}) ---\n${m.content}`).join('\n\n');
    let fullContext = `${curricularContext}\n\n${attachedMaterials}`;
    
    if (customFocus.trim()) {
      fullContext += `\n\n--- INSTRUCCIONES ESPECÍFICAS DEL DOCENTE ---\n${customFocus.trim()}`;
    }
    
    await onGenerate({
      topic: topic.trim(),
      age: level === 'superior' ? 'Universitario / Superior' : age,
      class_structure: classStructure, 
      context_text: fullContext,
      context_type: materials.length > 0 ? 'multi' : 'none',
      subject: finalSubject, 
      level: backendLevel,
      career: career
    });
  };

  const groupedRepoItems = repoItems.reduce((acc, item) => {
    if (item.is_annual_plan) return acc; 
    const f = item.folder || "General";
    if (!acc[f]) acc[f] = [];
    acc[f].push(item);
    return acc;
  }, {});
  const annualPlans = repoItems.filter(item => item.is_annual_plan);

  return (
    <form onSubmit={handleSubmit} className="space-y-6 w-full max-w-full overflow-hidden">
      
      {/* Topic y Enfoque Opcional */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-semibold text-slate-300 mb-2">
            ¿Qué tema tenés que enseñar hoy?
          </label>
          <input
            type="text"
            value={topic}
            onChange={e => setTopic(e.target.value)}
            placeholder="Ej: Revolución de Mayo, Ciclo del Agua, Programación en Bloques..."
            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all outline-none text-sm"
            required
          />
        </div>
        
        <div>
           <label className="block text-xs font-semibold text-slate-500 mb-1.5 flex items-center justify-between">
              Enfoque o dinámica deseada
              <span className="text-[10px] font-normal px-2 py-0.5 rounded bg-slate-800 border border-slate-700">Opcional</span>
           </label>
           <Textarea
              value={customFocus}
              onChange={e => setCustomFocus(e.target.value)}
              placeholder="Ej: Me gustaría que la clase tenga un enfoque lúdico usando cartas, o que se relacione con el uso de la tecnología..."
              className="bg-slate-800/50 border-slate-800 text-white placeholder-slate-600 resize-none min-h-[60px] text-xs rounded-xl focus:ring-1 focus:ring-blue-500/30"
           />
        </div>
      </div>

      {/* Grid: Contexto Curricular */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 space-y-4">
        <label className="text-sm font-semibold text-slate-300 flex items-center gap-2">
          <GraduationCap size={16} className="text-blue-400" />
          Contexto Curricular
        </label>

        {/* Level Selector */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'inicial', label: 'Nivel Inicial' },
            { id: 'primaria', label: 'Primaria' },
            { id: 'secundaria', label: 'Secundaria' },
            { id: 'superior', label: 'Superior / Universitario' }
          ].map(lvl => (
            <button
              key={lvl.id}
              type="button"
              onClick={() => handleLevelChange(lvl.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                level === lvl.id 
                  ? 'bg-blue-600 text-white border-blue-500 shadow-sm shadow-blue-500/20' 
                  : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500 hover:bg-slate-700'
              }`}
            >
              {lvl.label}
            </button>
          ))}
        </div>

        {/* Dynamic Selectors based on Level */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          
          {level === 'secundaria' && (
            <div className="space-y-1.5 md:col-span-2 lg:col-span-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Ciclo Escolar</label>
                <div className="flex gap-2">
                    <button type="button" onClick={() => { setCycle('basico'); setOrientation(''); setSubject(''); }} className={`flex-1 py-2 rounded-lg text-[10px] font-bold border transition-all ${cycle === 'basico' ? 'bg-blue-500/20 border-blue-500 text-blue-400' : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'}`}>CICLO BÁSICO</button>
                    <button type="button" onClick={() => { setCycle('orientado'); setSubject(''); }} className={`flex-1 py-2 rounded-lg text-[10px] font-bold border transition-all ${cycle === 'orientado' ? 'bg-violet-500/20 border-violet-500 text-violet-400' : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'}`}>CICLO ORIENTADO</button>
                </div>
            </div>
          )}

          {level !== 'superior' && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Edad de los alumnos</label>
              <div className="relative">
                <select 
                  value={age} 
                  onChange={e => setAge(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all appearance-none cursor-pointer"
                >
                  {AGES.map(a => (
                    <option key={a} value={a}>{a} años</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              </div>
            </div>
          )}
          
          {level === 'secundaria' && cycle === 'orientado' && (
             <div className="space-y-1.5">
               <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                 Orientación de la Escuela
               </label>
               <div className="relative animate-fade-in-up">
                 <select 
                   value={orientation} 
                   onChange={e => setOrientation(e.target.value)}
                   required
                   className="w-full bg-slate-950 border border-violet-500/50 rounded-xl p-3 text-sm text-violet-100 outline-none focus:border-violet-500 transition-all appearance-none cursor-pointer"
                 >
                   <option value="" disabled>Seleccioná la orientación...</option>
                   {ORIENTATIONS.map(ori => (
                     <option key={ori} value={ori}>{ori}</option>
                   ))}
                 </select>
                 <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-violet-500/50 pointer-events-none" />
               </div>
             </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              {level === 'superior' ? 'Área de Estudio' : 'Materia / Espacio Curricular'}
            </label>
            <div className="relative">
              <select 
                value={subject} 
                onChange={e => {
                  setSubject(e.target.value);
                  if(e.target.value !== 'Otro') setCustomSubject('');
                }}
                required
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all appearance-none cursor-pointer"
              >
                <option value="" disabled>Seleccioná la materia...</option>
                {(level === 'secundaria' && cycle === 'orientado' ? ORIENTED_SUBJECTS : SUBJECTS_BY_LEVEL[level === 'secundaria' ? `secundaria_${cycle}` : level])?.map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            </div>

            {subject === 'Otro' && (
              <div className="mt-2 animate-fade-in-up">
                <input
                  type="text"
                  value={customSubject}
                  onChange={e => setCustomSubject(e.target.value)}
                  placeholder="Escribí tu materia acá..."
                  required
                  className="w-full bg-slate-950 border border-blue-500/50 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all"
                />
              </div>
            )}
          </div>

          {level === 'superior' && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Carrera</label>
              <input
                type="text"
                value={career}
                onChange={e => setCareer(e.target.value)}
                placeholder="Ej: Abogacía, Ing. en Sistemas..."
                required
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all"
              />
            </div>
          )}

          {/* NUEVO: SELECTOR DE ESTRUCTURA INTEGRADO */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Estructura de Planificación</label>
            <div className="relative">
              <select 
                value={classStructure} 
                onChange={handleStructureSelect}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all appearance-none cursor-pointer font-medium"
              >
                {CLASS_STRUCTURES.map(struct => (
                  <option key={struct.id} value={struct.id}>
                    {struct.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            </div>
          </div>

        </div>
      </div>

      {/* Context Area */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 w-full max-w-full overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <label className="text-sm font-semibold text-slate-300 flex items-center gap-2">
            Material de referencia 
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${remainingSlots > 0 ? 'bg-blue-500/10 text-blue-400' : 'bg-red-500/10 text-red-400'}`}>
              {materials.length} / {limit === 999 ? '∞' : limit} usados
            </span>
          </label>
          <p className="text-[10px] text-slate-500 italic hidden sm:block">Cada archivo/URL consume 1 crédito</p>
        </div>

        {materials.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {materials.map((m) => (
              <div key={m.id} className="flex items-center gap-2 bg-slate-800 border border-blue-500/30 text-blue-200 px-2 py-1.5 rounded-lg text-xs animate-fade-in max-w-full">
                <div className="shrink-0">
                  {m.type === 'repo' ? <Library size={12} className="text-amber-400"/> : m.type === 'file' ? <FileArchive size={12} /> : m.type === 'url' ? <Link2 size={12} /> : <FileText size={12} />}
                </div>
                <span className="truncate max-w-[120px]">{m.name}</span>
                <button type="button" onClick={() => removeMaterial(m.id)} className="text-slate-500 hover:text-red-400 shrink-0">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <Tabs value={contextType} onValueChange={(val) => {
           if (val === 'repo' && !isUltra) {
             if (onOpenUltraPromo) onOpenUltraPromo();
             return;
           }
           setContextType(val);
        }} className="w-full max-w-full">
          
          <div className="w-full overflow-x-auto pb-1 mb-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <TabsList className="bg-slate-800 border border-slate-700 h-10 p-1 flex w-max min-w-full justify-start items-center">
              <TabsTrigger value="text" className="h-8 px-3 text-xs data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 flex items-center gap-1.5 shrink-0 transition-all">
                <FileText size={12} /> Texto
              </TabsTrigger>
              <TabsTrigger value="file" className="h-8 px-3 text-xs data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 flex items-center gap-1.5 shrink-0 transition-all">
                <Upload size={12} /> Documento
              </TabsTrigger>
              <TabsTrigger value="url" className="h-8 px-3 text-xs data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 flex items-center gap-1.5 shrink-0 transition-all">
                <Link2 size={12} /> Web / Video
              </TabsTrigger>
              <TabsTrigger value="repo" className={`h-8 px-3 text-xs flex items-center gap-1.5 shrink-0 transition-all ${isUltra ? 'data-[state=active]:bg-blue-600/20 data-[state=active]:border-blue-500/30 data-[state=active]:text-blue-400 text-slate-400' : 'text-slate-500'}`}>
                <Library size={12} className={isUltra ? 'text-blue-400' : ''} /> Mi Biblioteca
                {!isUltra && <Crown size={10} className="text-amber-500/70 ml-1" />}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="text" className="space-y-2">
            <Textarea
              value={contextText}
              onChange={e => setContextText(e.target.value)}
              placeholder="Escribí o pegá un fragmento de texto..."
              className="bg-slate-800 border-slate-700 text-white placeholder-slate-500 resize-none min-h-[80px] text-sm rounded-xl"
            />
            <button 
              type="button" 
              onClick={handleAddManualText} 
              disabled={!contextText.trim() || remainingSlots <= 0}
              className="w-full py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-30 text-white text-xs font-semibold rounded-lg transition-all"
            >
              Agregar Texto
            </button>
          </TabsContent>

          <TabsContent value="file">
            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                isProcessing ? 'opacity-50 pointer-events-none' : 'border-slate-700 hover:border-blue-500/40 bg-slate-800/30'
              }`}
              onClick={() => remainingSlots > 0 && fileRef.current?.click()}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-slate-500 border-t-blue-400 rounded-full animate-spin" />
                  <span className="text-slate-400 text-xs">Procesando...</span>
                </div>
              ) : remainingSlots > 0 ? (
                <>
                  <Upload size={20} className="mx-auto text-slate-600 mb-2" />
                  <p className="text-slate-400 text-xs">Subir archivo ({remainingSlots} disponibles)</p>
                  <p className="text-slate-500 text-[10px] mt-1 font-semibold">PDF, Word, Excel, PPT, Imágenes, TXT</p>
                </>
              ) : (
                <p className="text-amber-500 text-xs flex items-center justify-center gap-1">
                  <Info size={12} /> Límite alcanzado
                </p>
              )}
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); e.target.value = ''; }} />
          </TabsContent>

          <TabsContent value="url">
            <div className="flex gap-2 mb-2">
              <input
                type="url"
                value={contextUrl}
                onChange={e => setContextUrl(e.target.value)}
                placeholder="https://..."
                className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white text-sm outline-none w-full min-w-0"
              />
              <button
                type="button"
                onClick={handleUrlProcess}
                disabled={isProcessing || !contextUrl.trim() || remainingSlots <= 0}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white rounded-xl text-xs font-medium shrink-0"
              >
                Sumar
              </button>
            </div>
            <p className="text-slate-500 text-[10px] flex items-center gap-1 font-semibold">
               <Youtube size={12} className="text-red-400" /> Podés pegar enlaces de YouTube.
            </p>
          </TabsContent>

          <TabsContent value="repo">
             <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-4 max-h-[250px] overflow-y-auto custom-scrollbar space-y-4">
                {annualPlans.length > 0 && (
                   <div>
                     <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1"><Crown size={12}/> Planificaciones Anuales</p>
                     <div className="space-y-2">
                        {annualPlans.map(item => (
                           <div key={item.id} className="flex items-center justify-between bg-slate-950 p-2.5 rounded-lg border border-slate-700 hover:border-amber-500/30 transition-colors">
                              <div className="flex items-center gap-2 overflow-hidden pr-2">
                                <FileText size={14} className="text-amber-500 shrink-0" />
                                <span className="text-xs text-slate-300 truncate">{item.file_name}</span>
                              </div>
                              <button 
                                type="button" 
                                onClick={() => handleAddFromRepo(item)} 
                                disabled={remainingSlots <= 0}
                                className="text-[10px] bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded font-bold disabled:opacity-30 shrink-0"
                              >
                                Usar
                              </button>
                           </div>
                        ))}
                     </div>
                   </div>
                )}

                {Object.keys(groupedRepoItems).length > 0 ? (
                   <div>
                     <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-2 flex items-center gap-1"><Library size={12}/> Archivos por Materia</p>
                     <div className="space-y-2">
                        {Object.entries(groupedRepoItems).map(([folderName, items]) => (
                           <div key={folderName} className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
                              <button 
                                type="button" 
                                onClick={() => toggleRepoFolder(folderName)} 
                                className="w-full flex items-center justify-between p-2 hover:bg-slate-800 transition-colors"
                              >
                                <span className="text-xs font-bold text-slate-300 flex items-center gap-1.5"><ChevronRight size={12} className={`transition-transform ${expandedRepoFolders[folderName] ? 'rotate-90' : ''}`}/> {folderName}</span>
                                <span className="text-[9px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded">{items.length}</span>
                              </button>
                              
                              {expandedRepoFolders[folderName] && (
                                <div className="bg-slate-950 p-2 space-y-1">
                                  {items.map(item => (
                                     <div key={item.id} className="flex items-center justify-between bg-slate-900 p-2 rounded border border-slate-800">
                                        <span className="text-[10px] text-slate-400 truncate pr-2">{item.file_name}</span>
                                        <button 
                                            type="button" 
                                            onClick={() => handleAddFromRepo(item)} 
                                            disabled={remainingSlots <= 0}
                                            className="text-[9px] bg-slate-700 hover:bg-blue-600 text-white px-2 py-0.5 rounded font-bold disabled:opacity-30 shrink-0"
                                        >
                                            Añadir
                                        </button>
                                     </div>
                                  ))}
                                </div>
                              )}
                           </div>
                        ))}
                     </div>
                   </div>
                ) : (
                  annualPlans.length === 0 && (
                    <p className="text-xs text-slate-500 text-center py-4">Biblioteca vacía.</p>
                  )
                )}
             </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading || !topic.trim() || !subject.trim() || (level === 'secundaria' && cycle === 'orientado' && !orientation) || (subject === 'Otro' && !customSubject.trim()) || isProcessing}
        className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-40 text-white font-bold rounded-xl py-4 text-base transition-all flex items-center justify-center gap-3 shadow-lg shadow-blue-600/20"
      >
        {isLoading ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>Planificando en base al contexto...</span>
          </>
        ) : (
          <>
            <Sparkles size={20} strokeWidth={1.5} />
            <span>Generar Clase {materials.length > 0 ? `(${materials.length} mat.)` : ''}</span>
          </>
        )}
      </button>
    </form>
  );
}
